import React, { useState } from 'react';
import {
  BookOpen, Loader2, RefreshCcw, Zap, ChevronDown, ChevronRight,
} from 'lucide-react';
import * as api from '@/api/client';

import { apiErrorText } from '../../../lib/describeApiError';
import { stripMarkdown } from '../../../lib/stripMarkdown';
/** Shape of the `technical_understanding` blob produced by the
 *  `stage1_technical_understanding` LLM pass and persisted on
 *  `stage_state.content.technical_understanding`. Mirrored in the
 *  backend (server.py:_parse_technical_understanding). */
export type ResearchCues = {
  key_concepts?: string[];
  prior_art_questions?: string[];
  reading_suggestions?: string[];
  watch_points?: string[];
};

export type TechnicalUnderstanding = {
  raw?: string;
  plain_language_summary?: string;
  core_technical_concepts?: Array<{ term: string; definition: string }>;
  what_makes_this_different?: string;
  /** Four-bucket research orientation block — added in the v3
   *  prompt update. Backend parser fills any subsection the LLM
   *  produced; missing buckets come back as empty arrays. The
   *  card renders only the buckets that have content. */
  research_cues?: ResearchCues;
  generated_at?: string;
};

/**
 * "Basic Technical Understanding" card — now rendered fully open. The
 * previous collapsible version hid the content behind a click which
 * drafters found counter-intuitive (the tab already labelled the
 * content so hiding it again behind a second click added no signal).
 * The section-header styling stays to keep the visual anchor.
 */
/** Subsections rendered inside the Research Cues card. The keys
 *  match server.py:_parse_research_cues' output dict; the labels
 *  mirror the prompt's KEY CONCEPTS TO STUDY / PRIOR ART QUESTIONS
 *  / READING SUGGESTIONS / CLAIM DRAFTING WATCH-POINTS headers. */
const CUE_SECTIONS: Array<{ key: keyof ResearchCues; label: string; hint?: string }> = [
  { key: 'key_concepts',         label: 'Key concepts to study' },
  {
    key: 'prior_art_questions',
    label: 'Prior art questions',
    // Smart Q&A never asks about prior art, so Drafter Notes is the
    // input that carries these answers downstream (server.py
    // _get_strategy_block + the Stage 2 overview tag).
    hint: 'Have answers? Write them in the Drafter Notes tab — Novelty, '
      + 'Inventive Step, Strategy and claim drafting all read those notes.',
  },
  { key: 'reading_suggestions',  label: 'Reading suggestions' },
  {
    key: 'watch_points',
    label: 'Claim drafting watch-points',
    // Research cues are not passed to the Stage 2.5 strategy or the
    // Stage 3 claim prompts; only Drafter Notes are (as binding
    // directives via _get_strategy_block).
    hint: 'These are a checklist for you — they are not sent to the Strategy '
      + 'or claim drafting as instructions. To make the AI act on one, copy '
      + 'it into Drafter Notes.',
  },
];

function hasAnyCues(rc: ResearchCues | undefined): boolean {
  if (!rc) return false;
  return CUE_SECTIONS.some(({ key }) => Array.isArray(rc[key]) && (rc[key] as string[]).length > 0);
}

export function TechnicalUnderstandingCard({ tu }: { tu: TechnicalUnderstanding }) {
  const hasPlain = !!(tu.plain_language_summary || '').trim();
  const hasConcepts = !!(tu.core_technical_concepts || []).length;
  const hasDifferent = !!(tu.what_makes_this_different || '').trim();
  const hasCues = hasAnyCues(tu.research_cues);
  // Research cues are supplementary (drafter-orientation only —
  // not authoritative source data) but reviewers found them useful
  // enough to read every time, so they open by default. The toggle
  // stays so a drafter can fold them away.
  const [cuesOpen, setCuesOpen] = useState(true);
  if (!hasPlain && !hasConcepts && !hasDifferent && !hasCues && !tu.raw) return null;

  // The LLM text below is rendered as plain text, so markdown markers
  // ("**Novelty (per the IDF):**") are stripped for display only.
  return (
    <div
      data-testid="basic-technical-understanding"
      className="border border-zinc-200 rounded-sm bg-white"
    >
      <div className="flex items-center gap-2 px-6 pt-4 pb-2 border-b border-zinc-100">
        <BookOpen size={14} className="text-[#002FA7]" />
        <h3 className="text-xs font-mono uppercase tracking-widest text-zinc-500">
          Basic Technical Understanding
        </h3>
        <span className="text-[11px] text-zinc-400 font-normal">
          Orientation · plain-English · sourced from IDF
        </span>
      </div>
      <div className="px-6 pb-6 pt-4 space-y-5">
        {hasPlain && (
          <section data-testid="tu-plain-summary">
            <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
              Plain Language Summary
            </div>
            <p className="text-sm text-zinc-800 leading-relaxed whitespace-pre-wrap">
              {stripMarkdown(tu.plain_language_summary)}
            </p>
          </section>
        )}

        {hasConcepts && (
          <section data-testid="tu-core-concepts">
            <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-2">
              Core Technical Concepts
            </div>
            <dl className="space-y-2">
              {(tu.core_technical_concepts || []).map((c, i) => (
                <div key={i} className="grid grid-cols-[minmax(9rem,auto)_1fr] gap-x-3 gap-y-0.5">
                  <dt className="text-sm font-medium text-[#002FA7]">{stripMarkdown(c.term)}</dt>
                  <dd className="text-sm text-zinc-700 leading-relaxed">{stripMarkdown(c.definition)}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {hasDifferent && (
          <section data-testid="tu-what-different">
            <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
              What Makes This Different
            </div>
            <p className="text-sm text-zinc-800 leading-relaxed whitespace-pre-wrap">
              {stripMarkdown(tu.what_makes_this_different)}
            </p>
          </section>
        )}

        {hasCues && tu.research_cues && (
          <section data-testid="tu-research-cues">
            <button
              type="button"
              data-testid="tu-research-cues-toggle"
              onClick={() => setCuesOpen((v) => !v)}
              className="w-full flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-zinc-500 hover:text-zinc-700 transition-colors mb-2"
              aria-expanded={cuesOpen}
            >
              {cuesOpen
                ? <ChevronDown size={12} />
                : <ChevronRight size={12} />}
              <span>Research Cues</span>
              <span className="text-[10px] normal-case tracking-normal text-zinc-400 font-sans">
                · drafter orientation, supplementary
              </span>
            </button>
            {cuesOpen && (
              <div className="space-y-3">
                {CUE_SECTIONS.map(({ key, label, hint }) => {
                  const items = (tu.research_cues?.[key] || []) as string[];
                  if (!items.length) return null;
                  return (
                    <div key={key} data-testid={`tu-research-cues-${key}`}>
                      <div className="text-[11px] font-medium text-zinc-700 mb-1">
                        {label}
                      </div>
                      <ul className="list-disc pl-5 space-y-0.5">
                        {items.map((item, i) => (
                          <li
                            key={i}
                            className="text-sm text-zinc-700 leading-relaxed"
                          >
                            {stripMarkdown(item)}
                          </li>
                        ))}
                      </ul>
                      {hint && (
                        <p
                          data-testid={`tu-research-cues-${key}-hint`}
                          className="mt-1 text-[11px] text-zinc-500 italic"
                        >
                          {hint}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* Fallback: if parsing produced nothing, show the raw text
            rather than an empty card. Lets the drafter still read
            the LLM output even when the model ignored the section
            headers. */}
        {!hasPlain && !hasConcepts && !hasDifferent && !hasCues && tu.raw && (
          <pre
            data-testid="tu-raw"
            className="text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap font-sans"
          >
            {stripMarkdown(tu.raw)}
          </pre>
        )}
      </div>
    </div>
  );
}

/** Word count over the meaningful TU text. Used both for the read-time
 *  badge in the tab label and (potentially) by the empty-state copy. */
function tuWordCount(tu: TechnicalUnderstanding | undefined | null): number {
  if (!tu) return 0;
  const parts: string[] = [];
  if (tu.plain_language_summary) parts.push(tu.plain_language_summary);
  if (tu.what_makes_this_different) parts.push(tu.what_makes_this_different);
  if (Array.isArray(tu.core_technical_concepts)) {
    for (const c of tu.core_technical_concepts) {
      if (c?.term) parts.push(c.term);
      if (c?.definition) parts.push(c.definition);
    }
  }
  if (tu.research_cues) {
    for (const k of ['key_concepts', 'prior_art_questions', 'reading_suggestions', 'watch_points'] as const) {
      const items = tu.research_cues[k];
      if (Array.isArray(items)) parts.push(...items);
    }
  }
  // Fallback to the raw text when no parsed sections are present (e.g.
  // an older extraction the parser couldn't break into headers).
  if (parts.length === 0 && tu.raw) parts.push(tu.raw);
  const text = parts.join(' ').trim();
  if (!text) return 0;
  return text.split(/\s+/).filter(Boolean).length;
}

/** Read-time string for the Technology Basics tab label. ~200 wpm,
 *  rounded up so a half-minute read still reports as "1 min". Returns
 *  the em-dash when there is no content yet, matching the screenshot
 *  where empty tabs collapse their helper text to "—". */
export function computeTechBasicsReadTime(
  tu: TechnicalUnderstanding | undefined | null,
): string {
  const wc = tuWordCount(tu);
  if (wc === 0) return '\u2014';
  const minutes = Math.max(1, Math.ceil(wc / 200));
  return `${minutes} min`;
}

interface TechBasicsTabProps {
  matterId: string;
  isFrozen: boolean;
  tu: TechnicalUnderstanding | undefined | null;
  onRefresh: () => Promise<void> | void;
  onError: (msg: string | null) => void;
}

/**
 * Tab wrapper for Technology Basics. Renders the existing
 * `TechnicalUnderstandingCard` unchanged when there's content, plus
 * its own [Generate] / [Regenerate] action that hits the dedicated
 * stage_1/technical_understanding/generate endpoint — independent of
 * the full Analyze-IDF pipeline so the drafter can refresh just the
 * orientation block without re-running scoring.
 */
export default function TechBasicsTab({
  matterId, isFrozen, tu, onRefresh, onError,
}: TechBasicsTabProps) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  // A tu object can come back truthy-but-empty (e.g. the backend
  // persisted an {} under stage_state.content.technical_understanding
  // after a parse failure, or kept a generated_at timestamp with no
  // body). Treat that as "no content yet" so the empty-state card
  // renders a Generate button instead of a silent blank page — the
  // previous ``tu ? Card : Empty`` branch went to Card which then
  // returned null because every section was empty.
  const hasContent = !!(
    tu && (
      (tu.plain_language_summary || '').trim()
      || ((tu.core_technical_concepts || []).length > 0)
      || (tu.what_makes_this_different || '').trim()
      || hasAnyCues(tu.research_cues)
      || (tu.raw || '').trim()
    )
  );
  const handleGenerate = async () => {
    if (isFrozen || busy) return;
    setBusy(true);
    setFailed(false);
    onError(null);
    try {
      await api.generateStage1TechnicalUnderstanding(matterId);
      await onRefresh();
    } catch (e: any) {
      onError(apiErrorText(e, 'Generate Technology Basics failed'));
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const emptyButtonLabel = busy
    ? 'Generating…'
    : failed ? 'Retry' : 'Generate Technology Basics';

  return (
    <div data-testid="stage-1-tech-basics-tab" className="space-y-4">
      {hasContent && (
        <div className="flex items-center justify-end">
          <button
            type="button"
            data-testid="tech-basics-regenerate-button"
            onClick={handleGenerate}
            disabled={isFrozen || busy}
            className="bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-2.5 py-1 text-xs font-medium transition-colors flex items-center gap-1.5 disabled:opacity-50"
          >
            {busy
              ? <Loader2 size={12} className="animate-spin" />
              : <RefreshCcw size={12} />}
            {busy ? 'Generating…' : 'Re-generate'}
          </button>
        </div>
      )}

      {hasContent ? (
        <TechnicalUnderstandingCard tu={tu as TechnicalUnderstanding} />
      ) : (
        <div
          data-testid="tech-basics-empty"
          className="border border-zinc-200 rounded-sm bg-white p-6 text-sm text-zinc-500"
        >
          <div className="flex items-center gap-2 mb-2 text-zinc-700">
            <BookOpen size={14} className="text-[#002FA7]" />
            <span className="font-medium">
              {failed ? 'Generation failed' : 'No technical understanding yet'}
            </span>
          </div>
          <p className="text-xs leading-relaxed mb-3">
            {failed
              ? 'The last attempt didn\'t complete. Click Retry to try again — the generator picks up from the current IDF without re-running scoring.'
              : 'The Basic Technical Understanding block is a plain-English orientation drawn from the IDF. It\'s read-only context for you — not passed downstream as authoritative data — so generating it is safe and doesn\'t overwrite anything.'}
          </p>
          <button
            type="button"
            data-testid="tech-basics-generate-button"
            onClick={handleGenerate}
            disabled={isFrozen || busy}
            className="bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5 disabled:opacity-50"
          >
            {busy
              ? <Loader2 size={12} className="animate-spin" />
              : <Zap size={12} />}
            {emptyButtonLabel}
          </button>
        </div>
      )}
    </div>
  );
}
